import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import html2pdf from "html2pdf.js";
import html2canvas from 'html2canvas';
import {useDispatch, useSelector} from "react-redux";

import './Canvas.css';
import featureAction from "../../redux/feature/actions";
import {compareArraysByName} from "../../utils/common";
import Table from '../pdf/Table';
import PdfFooter from "../pdf/PdfFooter";

import { useTranslation } from "react-i18next";

function useDebouncedResize(callback, delay = 200) {
  useEffect(() => {
    let timeoutId;

    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        callback();
      }, delay);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [callback, delay]);
}

const Canvas = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useDebouncedResize(() => {
    setWindowWidth(window.innerWidth);
  }, 300);

  const dispatch = useDispatch();

  const assetsPath = useSelector(state => state.Feature.assetsPath);
  const Logo = process.env.PUBLIC_URL + assetsPath + 'images/Logo.png';
  const settings = process.env.PUBLIC_URL + assetsPath + 'images/settings.svg';
  const remove = process.env.PUBLIC_URL + assetsPath + 'images/remove.svg';

  const baseUrl = useSelector(state => state.Feature.imageBaseUrl);

  const pdfContentRef = useRef();

  const [canvasImageBaseUrl, setCanvasImageBaseUrl] = useState('');

  const getDownloadPdfStatus = useSelector(state => state.Status.downloadPdf);

  const [rectsOptions, setRectsOptions] = useState([
    { left: 720, top: 70, width: 200, height: 10 },
    { left: 100, top: 300, width: 200, height: 200 },
    { left: 400, top: 200, width: 200, height: 200 },
    { left: 700, top: 300, width: 200, height: 200 },
  ]);

  const screenWidth = window.innerWidth;
  const rootWidth = document.getElementById('root').offsetWidth;
  const leftPositionMargin = (screenWidth - rootWidth) / 2;

  const [rects, setRects] = useState([]);
  const coloursOnCanvas = useSelector(state => state.Feature.coloursOnCanvas);
  const updatedColour = useSelector(state => state.Feature.updatedColour);

  const canvasRef = useRef(null);
  const [canvasInstance, setCanvasInstance] = useState(null);

  const htmlRefs = useRef({});

  const today = new Date();
  const formattedDate = today.getFullYear() + '-' +
    String(today.getMonth() + 1).padStart(2, '0') + '-' +
    String(today.getDate()).padStart(2, '0');

  const { t, i18n } = useTranslation();
  const _projectName = useSelector(state => state.Feature.selectedObjectName);

  const [pdfContentVisibility, setPdfContentVisibility] = useState(false);

  // const storeCanvasImageInLocalStorage = (onCaptured) => {
  //   const div = document.querySelector('.acp-canvas-container');
  //   setTimeout(() => {
  //     html2canvas(div).then(canvas => {
  //       const dataUrl = canvas.toDataURL('image/png');
  //       setCanvasImageBaseUrl(dataUrl);
  //       if (typeof onCaptured === 'function') {
  //         onCaptured();
  //       }
  //     });
  //   }, 200);
  // };

  const removeItem = (id) => {
    dispatch(featureAction.confirmModalActionDefine({type: featureAction.REMOVE_COLOUR_0N_CANVAS, payload: id}));
    dispatch(featureAction.isOpenConfirmModal());
  }

  const updatedColourDefine = (id) => {
    dispatch(featureAction.updatedColourDataDefine({status: false,from:id, to:id, order: 0}));
  }

  const isOpenCanvasItemConfigModal = (id) => {
    updatedColourDefine(id);

    dispatch(
      featureAction.isOpenCanvasItemConfigModal()
    );
  };

  const getScaleInfo = () => {
    let width = document.getElementById('root').offsetWidth;
    let height = window.innerHeight;
    let widthScale = 0.65;
    let heightScale = 1;
    let minWidth = 768;

    if(width <= minWidth) {
      widthScale = 1;
      heightScale = 0.8;
    }

    return { 
      canvasWidth: width * widthScale, 
      canvasHeight: height * heightScale
    }
  }

  const formateHtmlRef = () => {
    for(let i = 0; i < rects.length; i++) {
      if(htmlRefs.current && rectsOptions[i]){
        if(htmlRefs.current[rects[i].id_product_attribute] !== null) {
          // Position label inside rectangle at top with 8px padding
          htmlRefs.current[rects[i].id_product_attribute].style.top = (rectsOptions[i].top + 8) + 'px';

          htmlRefs.current[rects[i].id_product_attribute].style.left = rectsOptions[i].left + 'px';

          htmlRefs.current[rects[i].id_product_attribute].style.width = rectsOptions[i].width + 'px';
        }
      }
    }
  }

  const repaintCanvas = () => {
    const canvas = canvasInstance;
    if (!canvas) return;

    canvas.clear();
    canvas.off('object:moving');
    canvas.off('object:scaling');
    canvas.off('object:modified');
    canvas.off('selection:created');
    canvas.off('selection:updated');
    canvas.renderAll();

    const imageCache = {};
    const loadImage = (url) =>
      new Promise((resolve) => {
        if (imageCache[url]) return resolve(imageCache[url]);
        fabric.Image.fromURL(url, (img) => {
          imageCache[url] = img;
          resolve(img);
        });
      });

    // Progressive image loading: load low-res first, then swap to high-res
    const loadImageProgressive = (lowResUrl, highResUrl, onLowRes, onHighRes) => {
      // Load low-res image first for quick display
      loadImage(lowResUrl).then((lowResImg) => {
        onLowRes(lowResImg);
        // Load high-res in background
        if (highResUrl !== lowResUrl) {
          loadImage(highResUrl).then((highResImg) => {
            onHighRes(highResImg);
          });
        }
      });
    };

    const pairs = [];

    const bringPairToFront = (cropRect) => {
      const pair = pairs.find((p) => p.cropRect === cropRect);
      if (!pair) return;
      pair.overlayImg.bringToFront();
      pair.cropRect.bringToFront();
      canvas.requestRenderAll();
    };

    if (rects.length > 0 && rects[0].visible) {
      const bgRect = rects[0];
      // Use medium image for quick load, full image for high quality
      const bgUrlLowRes = baseUrl + (bgRect.color_medium_image || bgRect.color_image);
      const bgUrlHighRes = baseUrl + bgRect.color_image;

      // Load low-res background first
      fabric.Image.fromURL(bgUrlLowRes, (bgImg) => {
        bgImg.set({
          left: 0,
          top: 0,
          scaleX: canvas.width / bgImg.width,
          scaleY: canvas.height / bgImg.height
        });
        canvas.setBackgroundImage(bgImg, canvas.renderAll.bind(canvas));

        // Then load high-res and swap
        if (bgUrlHighRes !== bgUrlLowRes) {
          fabric.Image.fromURL(bgUrlHighRes, (highResImg) => {
            highResImg.set({
              left: 0,
              top: 0,
              scaleX: canvas.width / highResImg.width,
              scaleY: canvas.height / highResImg.height
            });
            canvas.setBackgroundImage(highResImg, canvas.renderAll.bind(canvas));
          });
        }
      });
    } else {
      canvas.setBackgroundColor('white', canvas.renderAll.bind(canvas));
    }

    for (let i = 1; i < rects.length; i++) {
      const rectData = rects[i];
      const { id_product_attribute, color_image, color_medium_image, visible } = rectData;
      const { left, top, width, height } = rectsOptions[i];
      if (!visible) continue;

      const rectName = id_product_attribute || `rect_${i}`;

      // Use medium image for quick load
      const lowResUrl = baseUrl + (color_medium_image || color_image);
      const highResUrl = baseUrl + color_image;

      loadImage(lowResUrl).then((img) => {
        const scaleX = canvas.width / img.width;
        const scaleY = canvas.height / img.height;

        const overlayImg = new fabric.Image(img.getElement(), {
          left: 0,
          top: 0,
          scaleX,
          scaleY,
          selectable: false,
          evented: false
        });

        const clipRect = new fabric.Rect({
          left,
          top,
          width,
          height,
          angle: 0,
          originX: 'left',
          originY: 'top',
          absolutePositioned: true
        });

        overlayImg.clipPath = clipRect;
        canvas.add(overlayImg);

        const cropRect = new fabric.Rect({
          name: rectName,
          left,
          top,
          width,
          height,
          angle: 0,
          originX: 'left',
          originY: 'top',
          fill: 'rgba(0,0,0,0)',
          stroke: '#ccc',
          strokeWidth: 1,
          transparentCorners: false,
          hasRotatingPoint: true
        });

        canvas.add(cropRect);

        pairs.push({ cropRect, overlayImg, clipRect });

        // Load high-res image in background and swap
        if (highResUrl !== lowResUrl) {
          loadImage(highResUrl).then((highResImg) => {
            const highResScaleX = canvas.width / highResImg.width;
            const highResScaleY = canvas.height / highResImg.height;

            const newOverlayImg = new fabric.Image(highResImg.getElement(), {
              left: 0,
              top: 0,
              scaleX: highResScaleX,
              scaleY: highResScaleY,
              selectable: false,
              evented: false
            });

            newOverlayImg.clipPath = clipRect;

            // Find and update the pair with high-res image
            const pairIndex = pairs.findIndex(p => p.cropRect === cropRect);
            if (pairIndex >= 0) {
              canvas.remove(pairs[pairIndex].overlayImg);
              canvas.add(newOverlayImg);
              pairs[pairIndex].overlayImg = newOverlayImg;
              // Bring to correct z-order
              newOverlayImg.moveTo(canvas.getObjects().indexOf(cropRect) - 1);
              canvas.requestRenderAll();
            }
          });
        }

        const syncClip = () => {
          const newW = cropRect.width * cropRect.scaleX;
          const newH = cropRect.height * cropRect.scaleY;

          clipRect.set({
            left: cropRect.left,
            top: cropRect.top,
            width: newW,
            height: newH,
            angle: cropRect.angle,
            originX: 'left',
            originY: 'top',
            absolutePositioned: true
          });

          cropRect.set({
            width: newW,
            height: newH,
            scaleX: 1,
            scaleY: 1
          });

          overlayImg.dirty = true;
          canvas.requestRenderAll();
        };

        syncClip();

        cropRect.on('moving', syncClip);
        cropRect.on('scaling', syncClip);
        cropRect.on('rotating', syncClip);
        cropRect.on('modified', syncClip);

        cropRect.on('selected', () => {
          bringPairToFront(cropRect);
        });

        canvas.on('selection:created', (e) => {
          const obj = e.selected?.[0];
          if (obj && obj.type === 'rect') bringPairToFront(obj);
        });
        canvas.on('selection:updated', (e) => {
          const obj = e.selected?.[0];
          if (obj && obj.type === 'rect') bringPairToFront(obj);
        });

        const safeSyncLabel = (e, updateWidth = false) => {
          const obj = e.target;

          if (!obj) return;

          const ref = htmlRefs?.current ? htmlRefs.current[obj.name] : null;
          if (!ref) return;

          if (updateWidth) {
            ref.style.width = obj.width * obj.scaleX + 'px';
          }

          // Position label inside rectangle at top with 8px padding
          ref.style.top = (obj.top + 8) + 'px';
          ref.style.left = obj.left + 'px';
        };

        cropRect.on('moving', (e) => safeSyncLabel(e));
        cropRect.on('scaling', (e) => safeSyncLabel(e, true));
        cropRect.on('rotating', (e) => safeSyncLabel(e, true));
        cropRect.on('modified', (e) => safeSyncLabel(e, true));
      });
    }

    const scaleInfo = getScaleInfo();
    canvas.setWidth(scaleInfo.canvasWidth);
    canvas.setHeight(scaleInfo.canvasHeight);
    canvas.renderAll();
  };

  const generatePDF = () => {
      const element = pdfContentRef.current;
      const options = {
        margin: 0,
        filename: 'invoice.pdf',
        image: { type: 'jpeg', quality: 1 },
        html2canvas: {
          dpi: 96,
          letterRendering: true,
          scale: 1
        },
        jsPDF: {
          unit: 'px',
          format: [794, 1123],
          orientation: 'portrait'
        }
      };
  
      html2pdf().from(element).set(options).save();
  
      setTimeout(() => {
        setCanvasImageBaseUrl('')
        setPdfContentVisibility(false);
        formateHtmlRef();
        repaintCanvas();
      }, 300);

  };

  useEffect(() => {
    const scaleInfo = getScaleInfo();

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: scaleInfo.canvasWidth ,
      height: scaleInfo.canvasHeight,
      backgroundColor: '#ffffff',
    });

    setCanvasInstance(canvas);

    return () => {
      canvas.dispose();
    };
  }, []);

  useEffect(() => {
    let widthScale = rootWidth > 768 ? 0.65 : 1;
    let canvasWidth = rootWidth * widthScale;

    let unitWidth = canvasWidth / 20;
    let reactWidth = unitWidth * 4;
    let reactHeight = reactWidth;
    
    const bgRectLeftPosition = unitWidth * 14;
    const bgRectTopPosition = 70;

    const firstRectLeftPosition = unitWidth * 2;
    const firstRectTopPosition = 300;
    
    const secondRectLeftPosition = unitWidth * 2 + reactWidth + unitWidth * 2;
    const secondRectTopPosition = 200;

    const thirdRectLeftPosition = unitWidth * 2 + reactWidth + unitWidth * 2 + reactWidth + unitWidth * 2;
    const thirdRectTopPosition = 300;

    const reactPositionInit = [
      { left: bgRectLeftPosition, top: bgRectTopPosition, width: reactWidth, height: reactHeight },
      { left: firstRectLeftPosition, top: firstRectTopPosition, width: reactWidth, height: reactHeight },
      { left: secondRectLeftPosition, top: secondRectTopPosition, width: reactWidth, height: reactHeight },
      { left: thirdRectLeftPosition, top: thirdRectTopPosition, width: reactWidth, height: reactHeight },
    ];

    setRectsOptions([...reactPositionInit]);
  }, [windowWidth]);

  useEffect(() => {
    if(updatedColour.status) {
      try {
        dispatch(featureAction.updateUserColours());

        // If a column layout (1, 2, or 3) was selected, resize to fill that column
        const targetOrder = updatedColour.order;
        if (targetOrder >= 1 && targetOrder <= 3) {
          // Find the index of the color being edited
          const colorIndex = coloursOnCanvas.findIndex(r => r.id_product_attribute === updatedColour?.from?.id_product_attribute);
          if (colorIndex >= 0) {
            const scaleInfo = getScaleInfo();
            const columnWidth = scaleInfo.canvasWidth / 3;
            const columnHeight = scaleInfo.canvasHeight;

            setRectsOptions(prevOptions => {
              const newOptions = [...prevOptions];

              // Check if another color exists at the target position (targetOrder)
              if (targetOrder !== colorIndex && newOptions[targetOrder]) {
                // Swap: move the other color to edited color's current position
                newOptions[targetOrder] = { ...prevOptions[colorIndex] };
              }

              // Move edited color to fill the target column
              newOptions[colorIndex] = {
                left: (targetOrder - 1) * columnWidth,
                top: 0,
                width: columnWidth,
                height: columnHeight
              };

              return newOptions;
            });
          }
        }
      } catch (error) {
        console.error('change background error:', error.message);
      }
    }
  }, [updatedColour]);

  const _isSelectedObject = useSelector(state => state.Feature.isSelectedObject);
  useEffect(() => {
    console.log('here');
    const compareResult = compareArraysByName(coloursOnCanvas, rects);
    if(compareResult.onlyInArray1.length > 0) {
      setRects(prevState => [...prevState, compareResult.onlyInArray1[0]]);
    }

    if (compareResult.onlyInArray2.length > 0) {
      setRects(prevState => {
        return prevState.filter(r => r.name !== compareResult.onlyInArray2[0].name)
      });
    }

    if (compareResult.onlyInArray1.length === compareResult.onlyInArray2.length && compareResult.commonItems.length > 0) {
      setRects([...coloursOnCanvas]);
    }

    if(_isSelectedObject) {
      setRects([...coloursOnCanvas]);
      dispatch(featureAction.isSelectedObject());
    }

  }, [coloursOnCanvas]);

  useEffect(() => {
    formateHtmlRef();
    repaintCanvas();
  }, [rects, rectsOptions]);

  useEffect(() => {
    if (getDownloadPdfStatus) {

      setPdfContentVisibility(true);
      
      const div = document.querySelector('.acp-canvas-container');
      html2canvas(div).then(canvas => {
        const dataUrl = canvas.toDataURL('image/png');
        console.error('before setCanvasImageBaseUrl');
        setCanvasImageBaseUrl(dataUrl);
      });
    }
  }, [getDownloadPdfStatus]);

  useEffect(() => {
    if(canvasImageBaseUrl !== ''){
      console.error('after setCanvasImageBaseUrl');
      generatePDF();
    }
  }, [canvasImageBaseUrl]);

  
  return (
    <React.Fragment>
      <div className='acp-canvas-container'>
        <div></div>
        <canvas ref={canvasRef} id="clayCanvas"></canvas>
        {
          rects.map((rect, index) => {
              // Skip if no position data for this index
              if (!rectsOptions[index]) return null;
              // Position label inside rectangle at top with 8px padding
              const labelTop = rectsOptions[index].top + 8;
              return rect.visible && (<div
                id={rect.id_product_attribute}
                key={index}
                ref={(el) => (htmlRefs.current[rect.id_product_attribute] = el)}
                style={{
                  position: 'absolute',
                  top: `${labelTop}px`,
                  left: `${rectsOptions[index].left}px`,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  height: '24px',
                  width: `${rectsOptions[index].width}px`,
                }}
              >
                <div
                  key={index}
                  style={{
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    border: 'none',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontWeight: '500',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div>{rect.color_name}</div>
                    {
                      !getDownloadPdfStatus && (
                        <>
                          <img
                            src={settings}
                            alt={'setting'}
                            style={{ marginLeft: '8px', marginRight: '8px', cursor: 'pointer' }}
                            onClick={() => isOpenCanvasItemConfigModal(rect.id_product_attribute)}
                          />
                          <img
                            src={remove}
                            alt={'remove'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeItem(rect.id_product_attribute)}
                          />
                        </>
                      )
                    }
                </div>

              </div>)
            }
          )
        }

      </div>
      {pdfContentVisibility && (
        <div className='pdf-containter' ref={pdfContentRef}>     
          <>
            <div className='acp-pdf-header'>
              <div>{_projectName}</div>
              <div>
                <img src={Logo} alt='logo'/>
              </div>
              <div>{formattedDate}</div>
            </div>
            <div className='canvas-image-container'>
              <img src={canvasImageBaseUrl} alt="canvas image"></img>
            </div>
            <Table />
            <div style={{position:'absolute', bottom: '10px'}}>
              <PdfFooter />
            </div>
          </>
        </div>
      )}        
    </React.Fragment>
  );
};

export default Canvas;
